/**
 * Single source of truth for this deployment's product identity.
 *
 * Every call site that used to hardcode "LangWatch", a langwatch.ai URL, or
 * the LangWatch logo assets reads from here instead. To rebrand the product,
 * change the values below — no other file should hardcode a product name,
 * support/docs URL, or vendor email domain again. See
 * specs/branding/whitelabel-branding.feature.
 *
 * Safe to import from both client (Vite) and server (tsx) code: it has no
 * runtime dependencies and reads no env vars, so it behaves identically in
 * both bundlers.
 */
export const branding = {
  /** Shown in the browser tab title, page headers, and empty states. */
  productName: "VoxTest",
  /** The company operating this deployment. */
  companyName: "Voicing AI",
  /**
   * What the in-product assistant is called in copy: menu entries, error
   * copy, shortcut help, version author labels, its own API key name and
   * the system prompts that tell the model who it is. Identifiers (flag
   * keys, routes, file names, permissions) are not copy and keep their
   * names.
   */
  assistantName: "Vox",
  /**
   * What the analytics query language is called in copy (API descriptions,
   * error copy). Identifiers keep their `lwql` names.
   */
  queryLanguageName: "VoxTestQL",
  /** Title of the served OpenAPI specification. */
  apiTitle: "VoxTest API",
  /**
   * Prefix of the environment variables that snippets and key dialogs
   * print: `<envPrefix>_API_KEY`, `<envPrefix>_PROJECT_ID`,
   * `<envPrefix>_ENDPOINT`. Snippets pass these values to the SDK
   * explicitly, so the SDK's own variable names never reach the screen.
   */
  envPrefix: "VOXTEST",
  /** Id the hosted MCP server is registered under in a coding agent's config. */
  mcpServerId: "voxtest",
  /**
   * Prefix of newly minted API keys. Keys minted under the previous prefix
   * keep authenticating; see `~/server/api-key/api-key-token.utils.ts`.
   */
  apiKeyPrefix: "sk-vx-",
  /** Prefix of the ingestion-only key variant. */
  ingestionKeyPrefix: "ik-vx-",
  /**
   * Prefix of the webhook headers the platform sends (`<headerPrefix>-Signature`,
   * `<headerPrefix>-Event`, ...). The previous vendor-named headers are sent
   * alongside for one release so existing verifiers keep working.
   */
  headerPrefix: "X-VoxTest",
  /**
   * Namespace under which the SDK's `langwatch.*` trace attribute keys are
   * displayed (`voxtest.input`). Display only: the stored and searched key
   * is unchanged. See `~/features/traces-v2/utils/attributeDisplayName.ts`.
   */
  attributeNamespace: "voxtest",
  /**
   * Namespace under which `langevals/*` evaluator ids are displayed when the
   * id itself (not its human name) is shown. Display only.
   */
  evaluatorNamespace: "voxtest",
  /**
   * The published observability SDK package, on npm and PyPI. The one
   * deliberate place a vendor name still reaches the screen: an install
   * line and an import statement. Swap it here the day renamed packages
   * are published.
   */
  sdkPackageName: "langwatch",
  /**
   * The command a customer types to run the installed CLI. The published
   * package installs the CLI under two binaries, `langwatch` and `lw`; the
   * short one carries no vendor name, so every "run ... login" line uses
   * it. Installing the CLI still names `sdkPackageName`, and so does `npx`,
   * which resolves a package rather than a binary.
   */
  cliCommand: "lw",
  /**
   * The GitHub organisation that hosts agent skills and the Go SDK. Unset
   * hides every snippet that would point a customer at a vendor's GitHub.
   */
  githubOrgUrl: undefined as string | undefined,
  /** Support inbox used as the mailer's default From address and contact links. */
  supportEmail: "support@voicing.ai",
  /** Domain used when a mailer default-From falls back to `mailer@<domain>`. */
  emailDomain: "voicing.ai",
  /**
   * Base URL for product documentation. Unset until Voicing AI publishes a
   * product docs site: while it is unset every docs link in the app (built
   * through `~/utils/docsUrl`) is left out rather than pointing at a page
   * that does not exist. Set it to bring them all back at once. Keep it on
   * the confirmed `emailDomain`, since the link-trust logic in
   * `~/features/langy/logic/langyLinkDestination.ts` treats every subdomain
   * of `emailDomain` as this deployment's own.
   */
  docsBaseUrl: undefined as string | undefined,
  /** Status page linked from the support menu. Unset hides that menu entry. */
  statusPageUrl: undefined as string | undefined,
  /** Community / issue-tracker links for the support menu. Unset hides the entry. */
  githubDiscussionsUrl: undefined as string | undefined,
  githubIssuesUrl: undefined as string | undefined,
  discordUrl: undefined as string | undefined,
  /**
   * "Contact sales" always renders somewhere clickable, so it defaults to a
   * mailto rather than being left undefined.
   */
  contactSalesUrl: "mailto:support@voicing.ai",
  /**
   * This deployment's own address, used only where no configured host
   * (`BASE_HOST`, `NEXTAUTH_URL`) or page origin is known: code snippets,
   * fallback links in messages. Snippets rendered on a running app keep using
   * that app's own address.
   */
  appUrl: "https://voxtest.voicing.ai",
  /** The legal documents the sign-up, onboarding and home fine print link to. */
  termsUrl: "https://www.voicing.ai/terms-of-use-of-the-voicing-ai-software/",
  privacyPolicyUrl: "https://www.voicing.ai/privacy-policy",
  /**
   * VoxTest has no self-serve purchase flow yet. Left undefined so the
   * "Purchase license" button hides itself rather than pointing at LangWatch's
   * Stripe link.
   */
  licensePurchaseUrl: undefined as string | undefined,
  /**
   * Daily self-hosted usage telemetry receiver
   * (`~/server/usageStatsWorker.ts`). LangWatch's own worker defaulted this to
   * its own production endpoint (`https://app.langwatch.ai/api/track_usage`),
   * which would have every VoxTest deployment silently reporting org names,
   * hostnames, and usage counts to LangWatch's servers. Left undefined so the
   * worker skips sending entirely until Voicing AI stands up its own
   * telemetry receiver (if it wants one at all).
   */
  usageStatsEndpoint: undefined as string | undefined,
} as const;

export type Branding = typeof branding;
